import { Link } from 'react-router';
import { LandingPagesFooter as Footer, ScrollReveal } from '@podus/ui';
import { getReferralLinkBase, PRODUCT_URLS } from '@podus/utils';
import Navbar from '@/components/landing/Navbar';

/**
 * Public marketing page for the ambassador programme.
 *
 * Layout follows podus.org: a `page-scroll-root` shell, a 1920px-capped `main`,
 * and sections that carry their own `px-4 lg:px-16 xl:px-20` gutters rather
 * than nesting a narrower container inside.
 */
export default function Home() {
    // Resolved inside the component, not at module scope: `PRODUCT_URLS` and
    // friends use lazy getters so the environment is read at access time.
    // In dev this becomes `localhost:3010/ab`, matching the link an ambassador
    // will actually be handed.
    const referralBase = getReferralLinkBase().replace(/^https?:\/\//, '');

    const steps = [
        {
            title: 'Get invited',
            body: 'The programme is invite-only. A Podus admin sends you an invitation, and you set your password and claim your link.',
        },
        {
            title: 'Share your link',
            body: `Your link is yours: ${referralBase}/yourname. Post it, message it, print it as a QR code — however you already reach people.`,
        },
        {
            title: 'Watch it land',
            body: 'Every click and every signup that comes through your link is attributed to you and shows up on your dashboard.',
        },
    ];

    return (
        <div className="page-scroll-root bg-white">
            <Navbar />

            <main className="mx-auto max-w-[1920px] pb-4 pt-[10px] lg:pb-0">
                {/* ── Hero ── */}
                <section className="flex min-h-[calc(100dvh-68px)] flex-col px-4 pt-4 lg:min-h-[calc(100dvh-76px)] lg:px-16 lg:pt-0 xl:px-20">
                    <div className="flex flex-1 flex-col justify-center py-10 lg:flex-row lg:items-center lg:gap-16 lg:py-0">
                        <div className="w-full lg:max-w-[55%] lg:flex-1">
                            <ScrollReveal variant="fade-up" duration={800}>
                                <h1 className="text-balance">
                                    <span className="mb-4 block text-xs font-semibold uppercase tracking-[0.18em] text-primary sm:text-sm">
                                        Invite-only · Podus Ambassador
                                    </span>
                                    <span className="block text-hero-1 font-bold text-[#AEAEAE]">Share justice.</span>
                                    <span className="block text-hero-2 font-normal italic text-secondary">
                                        Track your impact.
                                    </span>
                                </h1>
                            </ScrollReveal>

                            <ScrollReveal variant="fade-up" delay={200} duration={700}>
                                <div className="mt-8 flex flex-col gap-1 sm:mt-12 sm:flex-row sm:items-start sm:gap-3">
                                    <span className="text-xl leading-none text-primary">&rarr;</span>
                                    <p className="max-w-[60ch] text-pretty text-lead text-[#434343]">
                                        Podus Ambassadors bring people to affordable legal help — and every one of them
                                        is attributed to you. One link, permanent attribution, and a dashboard that
                                        shows exactly how far your sharing reaches.
                                    </p>
                                </div>
                            </ScrollReveal>

                            <ScrollReveal variant="fade-up" delay={350}>
                                <div className="mt-10 flex flex-col gap-3 sm:flex-row sm:items-center">
                                    <Link
                                        to="/sign-in"
                                        className="flex items-center justify-center rounded-full bg-secondary px-8 py-3.5 text-sm font-semibold text-white transition-transform hover:scale-[1.02] active:scale-[0.98] sm:w-fit sm:px-12 sm:py-4 sm:text-[15px]"
                                    >
                                        Ambassador sign in
                                    </Link>
                                    <a
                                        href="#how-it-works"
                                        className="flex items-center justify-center rounded-full border border-[#E0E0E0] px-8 py-3.5 text-sm font-semibold text-secondary transition-colors hover:bg-[#f8f8f8] sm:w-fit sm:px-10 sm:py-4 sm:text-[15px]"
                                    >
                                        How it works
                                    </a>
                                </div>
                            </ScrollReveal>
                        </div>

                        {/* Link preview card */}
                        <ScrollReveal variant="fade-left" delay={300} className="mt-14 w-full lg:mt-0 lg:flex-1">
                            <div className="rounded-3xl border border-[#EEEEEE] bg-[#F5F5F5] p-5 sm:p-8 xl:p-10">
                                <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[#9a9a9a] sm:text-xs">
                                    Your referral link
                                </p>
                                <p className="mt-3 break-all font-mono text-base font-bold text-secondary sm:text-xl lg:text-[22px] xl:text-[26px]">
                                    {referralBase}/<span className="text-primary">yourname</span>
                                </p>

                                <dl className="mt-7 grid grid-cols-3 gap-3 border-t border-[#E0E0E0] pt-6 sm:mt-8 sm:gap-4">
                                    {[
                                        { label: 'Clicks', value: '1,284', accent: false },
                                        { label: 'Signups', value: '317', accent: true },
                                        { label: 'Rate', value: '24.7%', accent: false },
                                    ].map((stat) => (
                                        <div key={stat.label}>
                                            <dt className="text-[10px] uppercase tracking-wide text-[#9a9a9a] sm:text-[11px]">
                                                {stat.label}
                                            </dt>
                                            {/* Steps down at 320–375px so three columns of figures
                                                never collide inside the card. */}
                                            <dd
                                                className={`mt-1 text-xl font-bold leading-none tabular-nums sm:text-[26px] xl:text-[32px] ${
                                                    stat.accent ? 'text-primary' : 'text-secondary'
                                                }`}
                                            >
                                                {stat.value}
                                            </dd>
                                        </div>
                                    ))}
                                </dl>

                                <p className="mt-6 text-pretty text-xs leading-5 text-[#9a9a9a]">
                                    Illustrative figures. Your real numbers update as people click and sign up.
                                </p>
                            </div>
                        </ScrollReveal>
                    </div>
                </section>

                {/* ── How it works ── */}
                <section id="how-it-works" className="scroll-mt-24 px-4 py-16 lg:px-16 lg:py-24 xl:px-20">
                    <ScrollReveal variant="blur-up" delay={100} duration={800}>
                        {/* One fluid heading rather than podus.org's paired
                            desktop/mobile elements — same endpoints, half the DOM,
                            and no flat stretch across tablet widths. */}
                        <h2 className="text-balance text-section font-extrabold">
                            <span className="text-[#AEAEAE]">Three steps. </span>
                            <span className="text-secondary">That&rsquo;s the whole thing.</span>
                        </h2>
                    </ScrollReveal>

                    <div className="mt-10 grid gap-5 sm:gap-6 lg:mt-14 lg:grid-cols-3">
                        {steps.map((step, index) => (
                            <ScrollReveal key={step.title} variant="fade-up" delay={index * 120}>
                                <div className="h-full rounded-2xl border border-[#EEEEEE] p-6 sm:p-7 xl:p-9">
                                    <span className="flex h-11 w-11 items-center justify-center rounded-full bg-secondary text-[15px] font-bold text-white tabular-nums">
                                        {index + 1}
                                    </span>
                                    <h3 className="mt-6 text-[17px] font-bold text-secondary lg:text-[19px] xl:text-[22px]">
                                        {step.title}
                                    </h3>
                                    <p className="mt-3 text-pretty text-[15px] leading-7 text-[#747474] xl:text-base">
                                        {step.body}
                                    </p>
                                </div>
                            </ScrollReveal>
                        ))}
                    </div>
                </section>

                {/* ── Closing CTA ── */}
                {/* No top padding — the section above already ends on a full
                    `py-16 lg:py-24`, which is the whole gap. */}
                <section className="px-4 pb-16 lg:px-16 lg:pb-24 xl:px-20">
                    <ScrollReveal variant="fade-up">
                        <div className="rounded-3xl bg-secondary px-5 py-12 text-center sm:px-8 sm:py-14 lg:px-12 lg:py-20 xl:px-16">
                            <h2 className="mx-auto max-w-3xl text-balance text-section-sm font-extrabold">
                                <span className="text-white">Already invited? </span>
                                <span className="font-normal italic text-[#AEAEAE]">Pick up where you left off.</span>
                            </h2>

                            <p className="mx-auto mt-5 max-w-[52ch] text-pretty text-[15px] leading-7 text-[#B5B5B5] sm:text-base">
                                Sign in to grab your link, download your QR code and see how your referrals are doing.
                            </p>

                            <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:mt-9 sm:flex-row">
                                <Link
                                    to="/sign-in"
                                    className="w-full rounded-full bg-primary px-10 py-3.5 text-center text-sm font-semibold text-white transition-transform hover:scale-[1.02] sm:w-fit sm:px-12 sm:py-4 sm:text-[15px]"
                                >
                                    Sign in
                                </Link>
                                <a
                                    href={PRODUCT_URLS.podusai}
                                    className="w-full rounded-full border border-white/20 px-10 py-3.5 text-center text-sm font-semibold text-white transition-colors hover:bg-white/10 sm:w-fit sm:px-12 sm:py-4 sm:text-[15px]"
                                >
                                    Explore Podus AI
                                </a>
                            </div>
                        </div>
                    </ScrollReveal>
                </section>
            </main>

            <div className="mx-auto">
                <Footer />
            </div>
        </div>
    );
}
